import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, FileText, X, Download, RefreshCw, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ withCredentials: true });

/** Turn a stored URL into an openable link. R2 URLs are absolute; local-disk
 *  fallback returns relative `/api/uploads/…` paths. */
const resolveAssetUrl = (raw) => {
  if (!raw) return '';
  const s = String(raw).trim();
  if (/^(https?:)?\/\//i.test(s)) return s;
  return `${BACKEND_URL}${s.startsWith('/') ? '' : '/'}${s}`;
};

/** Local-disk fallback URLs are stored on the ephemeral pod filesystem and get
 *  wiped on every redeploy — so any `/api/uploads/...` URL from before the
 *  R2-only guard was deployed is almost certainly a 404 now. Admin sees a
 *  clear "please re-upload" affordance instead of a mysterious blank page. */
const isEphemeralUpload = (raw) => {
  if (!raw) return false;
  const s = String(raw).trim();
  return s.startsWith('/api/uploads/');
};

const FILE_SLOTS = [
  { urlKey: 'case_study_deck_url',   nameKey: 'case_study_deck_name',   label: 'Case Study Deck · Primary',        max: 25 },
  { urlKey: 'case_study_deck_url_2', nameKey: 'case_study_deck_name_2', label: 'Case Study Deck · Supplementary 1', max: 15 },
  { urlKey: 'case_study_deck_url_3', nameKey: 'case_study_deck_name_3', label: 'Case Study Deck · Supplementary 2', max: 15 },
  { urlKey: 'brand_approval_url',    nameKey: 'brand_approval_name',    label: 'Brand Approval',                   max: 15 },
];

const ACCEPT = '.pdf,.doc,.docx,.ppt,.pptx,.png,.jpg,.jpeg,.webp,application/pdf,image/*';

/**
 * Admin-side Supporting Files editor for a single nomination.
 *
 * Always renders the four slots even when the entry has no uploads — so
 * admins can help participants who couldn't complete their own upload.
 *
 * Props:
 *   nomination : the nomination row (needs `.id` + file fields)
 *   onChange   : called with the fresh nomination doc after every upload/clear
 */
const AdminSupportingFiles = ({ nomination, onChange }) => {
  const [busySlot, setBusySlot] = useState(null);
  const [progress, setProgress] = useState({});
  const inputRefs = useRef({});

  if (!nomination || !nomination.id) return null;

  const patchNomination = async (updates) => {
    const { data } = await ax.patch(`${API}/admin/nominations/${nomination.id}`, updates);
    if (typeof onChange === 'function') onChange(data);
    return data;
  };

  const upload = async (file, slot) => {
    if (!file) return;
    if (file.size > slot.max * 1024 * 1024) {
      toast.error(`File is ${(file.size / (1024 * 1024)).toFixed(1)} MB — this slot allows up to ${slot.max} MB.`);
      return;
    }
    setBusySlot(slot.urlKey);
    setProgress((p) => ({ ...p, [slot.urlKey]: 0 }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      // Deliberately no manual Content-Type — the browser adds the correct
      // multipart boundary automatically for FormData bodies.
      const r = await ax.post(`${API}/admin/uploads`, fd, {
        timeout: 180000,
        onUploadProgress: (evt) => {
          if (evt && evt.total) {
            setProgress((p) => ({ ...p, [slot.urlKey]: Math.round((evt.loaded / evt.total) * 100) }));
          }
        },
      });
      const url = r.data.url;
      const fname = r.data.filename;
      await patchNomination({ [slot.urlKey]: url, [slot.nameKey]: fname });
      toast.success(`${slot.label} uploaded.`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      const code = err?.code;
      let msg;
      if (typeof detail === 'string') msg = detail;
      else if (code === 'ECONNABORTED') msg = 'Upload timed out. Try a smaller file or a stronger network.';
      else if (status) msg = `Upload failed (HTTP ${status}). Please retry.`;
      else msg = 'Upload could not complete. Please retry.';
      toast.error(msg);
    } finally {
      setBusySlot(null);
      setProgress((p) => ({ ...p, [slot.urlKey]: 0 }));
    }
  };

  const clear = async (slot) => {
    if (!window.confirm(`Remove ${slot.label} from this entry?`)) return;
    try {
      // Empty string clears the field (NominationUpdate treats "" as clear).
      await patchNomination({ [slot.urlKey]: '', [slot.nameKey]: '' });
      toast.success('File removed from entry.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not clear file.');
    }
  };

  const hasAny = FILE_SLOTS.some((s) => nomination[s.urlKey]);

  return (
    <div className="mt-8 border-t border-[var(--gold-faint)] pt-6" data-testid="admin-supporting-files">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-[Libre_Baskerville] text-lg text-[var(--ink)]">Supporting Files</h3>
          <p className="text-[var(--ink-muted)] text-xs mt-1">
            Upload, replace or remove files on behalf of the entrant. Changes are saved immediately.
          </p>
        </div>
        {hasAny && (
          <a
            href={`${API}/admin/nominations/${nomination.id}/export.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] tracking-[0.3em] uppercase text-[var(--gold)] hover:text-[var(--gold-bright)] transition-colors inline-flex items-center gap-1"
            data-testid="admin-files-download-all"
          >
            <Download size={12} /> Entry PDF
          </a>
        )}
      </div>

      <div className="space-y-3">
        {FILE_SLOTS.map((slot) => {
          const url = nomination[slot.urlKey];
          const name = nomination[slot.nameKey];
          const busy = busySlot === slot.urlKey;
          const pct = progress[slot.urlKey] || 0;
          const openHref = url ? resolveAssetUrl(url) : '';
          const brokenLikely = isEphemeralUpload(url);
          return (
            <div key={slot.urlKey} className="border border-[var(--gold-faint)] p-3 bg-[var(--bg)]/40" data-testid={`admin-files-row-${slot.urlKey}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)]">{slot.label}</div>
                  {url ? (
                    <div className="mt-2 flex items-center gap-2 text-sm text-[var(--ink)] min-w-0">
                      <FileText size={14} className={brokenLikely ? 'text-[var(--terracotta)] shrink-0' : 'text-[var(--gold)] shrink-0'} />
                      <a
                        href={openHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`truncate transition-colors ${brokenLikely ? 'text-[var(--terracotta)] hover:text-[var(--terracotta-bright)]' : 'hover:text-[var(--gold)]'}`}
                        data-testid={`admin-files-open-${slot.urlKey}`}
                      >
                        {name || 'Uploaded file'}
                      </a>
                    </div>
                  ) : (
                    <div className="mt-2 text-sm text-[var(--ink-muted)] italic">No file uploaded.</div>
                  )}
                  {brokenLikely && (
                    <div
                      className="mt-2 flex items-start gap-2 text-[11px] leading-relaxed text-[var(--terracotta)] bg-[var(--terracotta)]/8 border border-[var(--terracotta)]/25 px-3 py-2"
                      data-testid={`admin-files-broken-${slot.urlKey}`}
                    >
                      <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                      <span>
                        This file was uploaded to temporary storage and is likely no longer available.
                        Please replace it — either upload the correct file here or ask the entrant to re-upload.
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <input
                    ref={(el) => { inputRefs.current[slot.urlKey] = el; }}
                    type="file"
                    accept={ACCEPT}
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) upload(f, slot);
                      e.target.value = '';
                    }}
                    data-testid={`admin-files-input-${slot.urlKey}`}
                  />
                  {url && (
                    <a
                      href={openHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      className="border border-[var(--gold-faint)] hover:border-[var(--gold)] text-[var(--ink)] hover:text-[var(--gold)] text-[10px] tracking-[0.3em] uppercase px-3 py-1.5 transition-colors inline-flex items-center gap-1.5"
                      data-testid={`admin-files-download-${slot.urlKey}`}
                    >
                      <Download size={12} /> Download
                    </a>
                  )}
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => inputRefs.current[slot.urlKey]?.click()}
                    className="border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--bg)] text-[10px] tracking-[0.3em] uppercase px-3 py-1.5 transition-colors disabled:opacity-50 inline-flex items-center gap-1.5"
                    data-testid={`admin-files-upload-${slot.urlKey}`}
                  >
                    {busy ? (
                      <>{pct > 0 && pct < 100 ? `Uploading ${pct}%` : 'Uploading…'}</>
                    ) : url ? (
                      <><RefreshCw size={12} /> Replace</>
                    ) : (
                      <><Upload size={12} /> Upload</>
                    )}
                  </button>
                  {url && !busy && (
                    <button
                      type="button"
                      onClick={() => clear(slot)}
                      className="text-[var(--terracotta)] hover:text-[var(--terracotta-bright)] transition-colors p-1"
                      title="Remove file"
                      data-testid={`admin-files-clear-${slot.urlKey}`}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>
              {busy && pct > 0 && (
                <div className="mt-2 h-[3px] w-full bg-[var(--gold-soft)]/30" data-testid={`admin-files-progress-${slot.urlKey}`}>
                  <div className="h-full bg-[var(--gold)] transition-all" style={{ width: `${pct}%` }} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminSupportingFiles;
